import {
  Box,
  Button,
  CircularProgress,
  DialogContent,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useCreditContext, useHandleApiCall } from '../../hooks';
import { API_CONSTANTS } from '../../utils';
import { CustomDialogContainer } from '../../../t2c-fe/Common/dialog';
import { NextImage } from '@sdf/base';
import { axiosInstance } from '../../utils';
import { useRouter } from 'next/router';

const CreditAssignment = ({
  paymentId,
  handleClose,
  isOpen,
  isAlphaPricingMode = false,
  setIsRedirecting,
}: {
  paymentId: string;
  handleClose: () => void;
  isOpen: boolean;
  isAlphaPricingMode?: boolean;
  setIsRedirecting?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const { get } = useHandleApiCall();
  const [isCompleted, setIsCompleted] = useState(false);
  const { data, isError, isLoading } = get(
    [paymentId],
    `${API_CONSTANTS.PAYMENT_STATUS.path}/${paymentId}`,
    {},
    15000,
    API_CONSTANTS.PAYMENT_STATUS.keyToInvalidate,
    !isCompleted
  );

  const { handleUpdateUserCredit } = useCreditContext();
  const [isGuest, setIsGuest] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const { query, back } = useRouter();
  const from = query?.from;
  // const progressItems = isAlphaPricingMode
  //   ? ['Activating trial access', 'Applying credits']
  //   : ['Confirming payment', 'Applying credits'];
  // const completedItems = isAlphaPricingMode
  //   ? ['Trial access is live', 'Credits are ready']
  //   : ['Payment is confirmed', 'Credits are ready'];

  // Fetch current user to know if guest and email value
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axiosInstance.get('/api/users/get-user');
        setIsGuest(!!res.data?.isGuest);
      } catch {}
    };
    if (isOpen) fetchUser();
  }, [isOpen]);

  useEffect(() => {
    if (!isLoading && !isError && data?.status === 'Completed') {
      setIsCompleted(true);
      handleUpdateUserCredit();
      if (from === 'last') {
        back();
      }
      if (setIsRedirecting) {
        setIsRedirecting(true);
      }
    }
  }, [isLoading, isError, data]);

  return (
    <CustomDialogContainer
      PaperProps={{
        sx: {
          border: (theme) => `0.5px solid ${theme.palette.background.borderV2}`,
          backgroundColor: (theme) => `${theme.palette.background.primaryV2} !important`,
        },
      }}
      sx={{
        '& .MuiPaper-root.MuiDialog-paper': {
          backgroundColor: (theme) => theme.palette.background.primaryV2,
        },
      }}
      open={isOpen}
    >
      <DialogContent sx={{ backgroundColor: 'transparent', px: { xs: 2.5, sm: 3 }, py: 3 }}>
        <Box display="flex" alignItems="center" flexDirection="column" padding={1}>
          {isCompleted ? (
            <>
              <NextImage
                src={'/assets/icons/payment-done.svg'}
                alt=""
                width={80}
                height={80}
                sx={{ mb: 2.75 }}
              />
              <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.75, textAlign: 'center' }}>
                {isAlphaPricingMode ? 'Your free trial is active' : 'Payment confirmed'}
              </Typography>
              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  opacity: '0.75',
                  fontSize: '15px',
                  maxWidth: 400,
                }}
              >
                {isAlphaPricingMode
                  ? 'Your free trial is active and credits are available to use now.'
                  : 'Payment is complete and credits have been added to your account.'}
              </Typography>

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                mt={2.25}
                width="100%"
                maxWidth={460}
              >
                <Box
                  sx={{
                    flex: 1,
                    border: (theme) => `0.5px solid ${theme.palette.background.borderV2}`,
                    backgroundColor: (theme) => theme.palette.background.ternaryV2,
                    borderRadius: '10px',
                    px: 1.5,
                    py: 1.15,
                  }}
                >
                  <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.4 }}>
                    Charged today
                  </Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {isAlphaPricingMode ? '$0.00' : 'Paid'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    border: (theme) => `0.5px solid ${theme.palette.background.borderV2}`,
                    backgroundColor: (theme) => theme.palette.background.ternaryV2,
                    borderRadius: '10px',
                    px: 1.5,
                    py: 1.15,
                  }}
                >
                  <Typography sx={{ fontSize: 11, opacity: 0.7, mb: 0.4 }}>Status</Typography>
                  <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                    {isAlphaPricingMode ? 'Trial active' : 'Credits applied'}
                  </Typography>
                </Box>
              </Stack>

              {/* <Stack spacing={1} mt={2.25} width="100%" maxWidth={460}>
                {completedItems.map((item) => (
                  <Box
                    key={item}
                    display="flex"
                    alignItems="center"
                    gap={1.25}
                    px={1.5}
                    py={1.1}
                    borderRadius="10px"
                    bgcolor={(theme) => theme.palette.background.ternaryV2}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: (theme) => theme.palette.success.main,
                        flexShrink: 0,
                      }}
                    />
                    <Typography sx={{ fontSize: 13, color: (theme) => theme.palette.text.primary }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack> */}

              {isGuest ? (
                <Box mt={1.75} width="100%" maxWidth={460}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      textAlign: 'center',
                      color: (theme) => theme.palette.text.lighterText2,
                    }}
                  >
                    Quick-start session active. You can finish account setup later from the
                    dashboard.
                  </Typography>
                </Box>
              ) : null}

              {/* Show receipt id if available */}
              {data?.receiptId ? (
                <Box
                  display="flex"
                  alignItems="center"
                  mt={2}
                  mb={1}
                  p="10px"
                  borderRadius={0.5}
                  width="100%"
                  maxWidth={460}
                  bgcolor={(theme) => theme.palette.background.ternaryV2}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', mr: 1, ml: '4px', wordBreak: 'break-all' }}
                  >
                    Receipt ID: <b>{data.receiptId}</b>
                  </Typography>
                  <Tooltip title={data.receiptId}>
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(data.receiptId);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      sx={{ ml: 1 }}
                    >
                      <ContentCopyIcon
                        fontSize="small"
                        sx={{ color: (theme) => theme.palette.text.primary }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              ) : null}

              <Box display="flex" flexDirection="column" gap={1} mt={3} width="100%" maxWidth={320}>
                <Button
                  variant="contained"
                  fullWidth
                  autoFocus
                  onClick={handleClose}
                  sx={{
                    color: (theme) => theme.palette.text.btnText,
                    boxShadow: 'none',
                    whiteSpace: 'nowrap',
                    minWidth: 100,
                    padding: '6px 64px',
                    '&:focus': {
                      outline: '2px solid #1976d2', // Visible focus ring
                    },
                  }}
                >
                  {isAlphaPricingMode ? 'Start creating' : 'Open dashboard'}
                </Button>
              </Box>

              {/* Aria-live for copy announcement */}
              <div aria-live="polite" style={{ position: 'absolute', left: '-10000px' }}>
                {copied ? 'Copied' : ''}
              </div>
            </>
          ) : (
            <>
              <CircularProgress size={50} sx={{ marginBottom: 2.75 }} />

              <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.75, textAlign: 'center' }}>
                {isAlphaPricingMode ? 'Finalizing your free trial' : 'Finalizing your payment'}
              </Typography>

              <Typography
                variant="body1"
                align="center"
                sx={{
                  color: (theme) => theme.palette.text.primary,
                  opacity: '0.75',
                  fontSize: '15px',
                  maxWidth: 400,
                }}
              >
                {isAlphaPricingMode ? (
                  <>
                    We are activating your trial and applying credits.
                    <br />
                    This usually finishes within <strong>1-2 minutes</strong>.
                  </>
                ) : (
                  <>
                    We are confirming your payment and applying credits.
                    <br />
                    This usually finishes within <strong>1-2 minutes</strong>.
                  </>
                )}
              </Typography>

              {/* <Stack spacing={1} mt={2.25} width="100%" maxWidth={460}>
                {progressItems.map((item, index) => (
                  <Box
                    key={item}
                    display="flex"
                    alignItems="center"
                    gap={1.25}
                    px={1.5}
                    py={1.1}
                    borderRadius="10px"
                    bgcolor={(theme) => theme.palette.background.ternaryV2}
                  >
                    {index === 0 ? (
                      <CircularProgress size={14} />
                    ) : (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: (theme) => theme.palette.text.lighterText,
                          opacity: 0.75,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <Typography sx={{ fontSize: 13, color: (theme) => theme.palette.text.primary }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Stack> */}

              <Typography
                sx={{
                  fontSize: 12,
                  textAlign: 'center',
                  color: (theme) => theme.palette.text.lighterText2,
                  maxWidth: 360,
                  mt: 2,
                }}
              >
                <>⚠️ Do not refresh, close, or switch tabs/windows to avoid any interruptions.</>
              </Typography>
            </>
          )}
        </Box>
      </DialogContent>
    </CustomDialogContainer>
  );
};

export default CreditAssignment;
